import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { useLanguage } from "../../components/LanguageContext";
import {
  Language,
  License,
  LicenseUser,
  PatchLicense,
  RemovedLicenseUser,
} from "../../Types/types";
import { LicenseTranslation } from "../../Translations/LicenseTranslation";
import { AppDispatch } from "../../components/Manual_List/fetch/store";
import {
  fetchLicenseById,
  patchLicense,
  postLicense,
} from "../../components/Manual_List/fetch/slices";
import PackageListHeader from "../../components/Headers/PackageListHeader";
import { GeneralTranslation } from "../../Translations/GeneralTranslation";
import LicenseUsers from "./LicenseUsers";
import CircularProgress from "@mui/material/CircularProgress";

const AddLicense: React.FC = () => {
  const { id } = useParams<{ id?: string }>();
  const { language } = useLanguage();
  const t = LicenseTranslation[language as Language];
  const gt = GeneralTranslation[language as Language];
  const navigate = useNavigate();
  const dispatch: AppDispatch = useDispatch();

  const [name, setName] = useState<string>("");
  const [email, setEmail] = useState<string>("");
  const [numberOfUsers, setNumberOfUsers] = useState<number>(1);
  const [expiredAt, setExpiredAt] = useState("");
  const [addedUsers, setAddedUsers] = useState<LicenseUser[] | null>();
  const [removedUsers, setRemovedUsers] = useState<
    RemovedLicenseUser[] | null
  >();

  const [isLoading, setIsLoading] = useState(false);
  const [numberOfUsersError, setNumberOfUsersError] = useState<string>("");
  const [nameError, setNameError] = useState<string>("");
  const [emailError, setEmailError] = useState<string>("");

  const formatDate = (dateString: any) => {
    if (dateString) {
      const date = new Date(dateString.date);
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const day = String(date.getDate()).padStart(2, "0");
      return `${year}-${month}-${day}`;
    }
    return "";
  };

  useEffect(() => {
    if (id) {
      setIsLoading(true);
      dispatch(fetchLicenseById(id)).then((result) => {
        if (result.payload) {
          const licenseData = result.payload as License;

          setName(licenseData.name || "");
          setEmail(licenseData.email || "");
          setNumberOfUsers(licenseData.numberOfAccounts || 1);
          setExpiredAt(formatDate(licenseData.expiredAt) || "");
        }
      });
      setIsLoading(false);
    }
  }, [id, dispatch]);

  const handleNameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setName(event.target.value);
    if (nameError) {
      setNameError("");
    }
  };

  const handleEmailChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setEmail(event.target.value);
    if (emailError) {
      setEmailError("");
    }
  };

  const handleNumberOfUsers = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    if (numberOfUsersError) {
      setNumberOfUsersError("");
    }
    if (value === "") {
      setNumberOfUsers(0);
    } else {
      const numberOfUsers = parseInt(value, 10);
      if (!isNaN(numberOfUsers)) {
        setNumberOfUsers(numberOfUsers);
      }
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    setNameError("");
    setEmailError("");
    setNumberOfUsersError("");

    let hasError = false;

    if (!name || name.trim().length < 3) {
      setNameError("Name must be at least 3 characters long");
      hasError = true;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!email || !emailRegex.test(email)) {
      setEmailError("Please enter a valid email address");
      hasError = true;
    }

    if (!numberOfUsers || numberOfUsers <= 0) {
      setNumberOfUsersError(
        "Number of users is required and must be greater than 0"
      );
      hasError = true;
    }

    if (hasError) {
      return;
    }

    setIsLoading(true);

    if (id) {
      const patchLicenseData = {
        id: parseInt(id),
        licenseData: {
          name: name,
          email: email,
          numberOfUsers: numberOfUsers,
          expiredAt: expiredAt.length > 0 ? expiredAt : null,
        },
      };
      const patchResultAction = await dispatch(patchLicense(patchLicenseData));

      if (!patchLicense.fulfilled.match(patchResultAction)) {
        console.error("Failed to patch license:", patchResultAction.payload);
        return;
      }

      const resultPayload = patchResultAction.payload as PatchLicense;
      if (
        resultPayload.accounts?.addedAccounts ||
        resultPayload.accounts?.removedAccounts
      ) {
        setAddedUsers(resultPayload.accounts?.addedAccounts);
        setRemovedUsers(resultPayload.accounts?.removedAccounts);
      } else {
        navigate("/licenses-list");
      }
    } else {
      const licenseData = {
        name: name,
        email: email,
        numberOfUsers: numberOfUsers,
        expiredAt: expiredAt,
      };
      const resultAction = await dispatch(postLicense(licenseData));

      if (!postLicense.fulfilled.match(resultAction)) {
        console.error("Failed to post license:", resultAction.payload);
        return;
      }

      setAddedUsers(resultAction.payload.accounts);
    }

    setIsLoading(false);
  };

  return (
    <div className="min-h-screen w-screen flex flex-col bg-gray-100 dark:bg-darkBackground dark:text-white">
      <PackageListHeader />
      <div className="flex flex-col items-center justify-start flex-grow">
        {addedUsers ? (
          <LicenseUsers
            users={addedUsers}
            label={t.addedUsers.replace("{licenseName}", name)}
          />
        ) : removedUsers ? (
          <LicenseUsers
            users={removedUsers}
            label={t.removedUsers.replace("{licenseName}", name)}
            isPassword={false}
          />
        ) : (
          <form
            id="add-license-form"
            className="w-full px-4 sm:px-6 md:px-8 py-6 max-w-7xl flex flex-col"
            onSubmit={handleSubmit}
          >
            <div className="flex items-center gap-3 mb-6">
              <button
                type="button"
                onClick={() => navigate("/licenses-list/")}
                className="p-2 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-lg transition-colors"
              >
                <ArrowLeft
                  className="text-gray-600 dark:text-gray-400"
                  size={20}
                />
              </button>
              <h1 className="text-2xl font-semibold">
                {id ? t.titleEdit : t.titleAdd}
              </h1>
            </div>

            <div className="space-y-6">
              <div>
                <label
                  htmlFor="name"
                  className="block mb-2 text-sm font-medium text-gray-700 dark:text-gray-300"
                >
                  {gt.name}
                </label>
                <input
                  type="text"
                  id="name"
                  className={`w-full px-4 py-3 rounded-lg border ${
                    nameError
                      ? "border-red-500 focus:border-red-500 focus:ring-red-500"
                      : "border-gray-300 dark:border-gray-600 focus:border-purple focus:ring-purple"
                  } bg-white dark:bg-darkBackground shadow-sm outline-none focus:ring-1 transition-colors`}
                  value={name}
                  onChange={handleNameChange}
                  autoComplete="off"
                  placeholder="Enter license name"
                />
                {nameError && (
                  <p className="text-red-500 text-sm mt-2">{nameError}</p>
                )}
              </div>

              <div>
                <label
                  htmlFor="email"
                  className="block mb-2 text-sm font-medium text-gray-700 dark:text-gray-300"
                >
                  {t.email}
                </label>
                <input
                  id="email"
                  type="email"
                  className={`w-full px-4 py-3 rounded-lg border ${
                    emailError
                      ? "border-red-500 focus:border-red-500 focus:ring-red-500"
                      : "border-gray-300 dark:border-gray-600 focus:border-purple focus:ring-purple"
                  } bg-white dark:bg-darkBackground shadow-sm outline-none focus:ring-1 transition-colors`}
                  value={email}
                  onChange={handleEmailChange}
                  autoComplete="off"
                  placeholder="Enter email address"
                />
                {emailError && (
                  <p className="text-red-500 text-sm mt-2">{emailError}</p>
                )}
              </div>

              <div>
                <label
                  htmlFor="numberOfUsers"
                  className="block mb-2 text-sm font-medium text-gray-700 dark:text-gray-300"
                >
                  {t.numberOfUsers}
                </label>
                <input
                  id="numberOfUsers"
                  type="number"
                  className={`w-full px-4 py-3 rounded-lg border ${
                    numberOfUsersError
                      ? "border-red-500 focus:border-red-500 focus:ring-red-500"
                      : "border-gray-300 dark:border-gray-600 focus:border-purple focus:ring-purple"
                  } bg-white dark:bg-darkBackground shadow-sm outline-none focus:ring-1 transition-colors`}
                  value={numberOfUsers || ""}
                  onChange={(e) => handleNumberOfUsers(e)}
                  autoComplete="off"
                  placeholder="Enter number of users"
                  min="1"
                />
                {numberOfUsersError && (
                  <p className="text-red-500 text-sm mt-2">
                    {numberOfUsersError}
                  </p>
                )}
              </div>

              <div>
                <label
                  htmlFor="validUntil"
                  className="block mb-2 text-sm font-medium text-gray-700 dark:text-gray-300"
                >
                  {t.validUntil}
                </label>
                <input
                  id="validUntil"
                  type="date"
                  className="w-full px-4 py-3 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-darkBackground shadow-sm outline-none focus:border-purple focus:ring-1 focus:ring-purple transition-colors"
                  value={expiredAt || ""}
                  onFocus={(event) => event.target.showPicker()}
                  onChange={(e) => setExpiredAt(e.target.value)}
                  autoComplete="off"
                />
              </div>
            </div>

            <div className="flex justify-end gap-3 mt-8 pt-6">
              <button
                type="button"
                className="bg-gray-300 dark:bg-white dark:text-black text-gray-700 px-6 py-2.5 rounded-lg font-medium shadow-sm hover:bg-opacity-90 transition-all"
                onClick={() => navigate(-1)}
              >
                {gt.discard}
              </button>
              <button
                type="submit"
                className="bg-purple text-white px-6 py-2.5 rounded-lg font-medium shadow-sm hover:bg-opacity-90 transition-all"
                form="add-license-form"
              >
                {gt.save}
              </button>
            </div>
          </form>
        )}
      </div>
      {isLoading && (
        <div className="absolute inset-0 bg-gray-100 bg-opacity-75 flex justify-center items-center z-50">
          <CircularProgress />
        </div>
      )}
    </div>
  );
};

export default AddLicense;
